import {
  DEFAULT_TRACE_OPTIONS,
  renderNeonPreview,
  traceImage,
  type TraceOptions,
  type TraceResult,
} from "../neon/traceImage";
import { NEON_PALETTE } from "../neon/types";

type PreviewMode = "source" | "mask" | "trace" | "neon";

const PREVIEW_MODES: ReadonlyArray<{ id: PreviewMode; label: string; hint: string }> = [
  { id: "source", label: "Source", hint: "Downsampled input, no processing." },
  { id: "mask", label: "Mask", hint: "Binary threshold — tune threshold + invert here." },
  { id: "trace", label: "Trace", hint: "Detected contours overlaid on the source." },
  { id: "neon", label: "Neon", hint: "How the imported tubes will read." },
];

// Mirrors App.UNITS_PER_INCH — 1 scene unit ≈ 4 inches.
const UNITS_PER_INCH = 1 / 4;

export interface ImportPayload {
  /** Scene-space point arrays, one per contour. */
  contours: [number, number, number][][];
  color: string;
  replace: boolean;
}

export interface ImportDialogHooks {
  onImport(payload: ImportPayload): void;
  /** Fires on any dialog dismissal — cancel, import, or Esc. */
  onClose?(): void;
}

interface Controls {
  threshold: HTMLInputElement;
  thresholdLabel: HTMLElement;
  autoThreshold: HTMLButtonElement;
  invert: HTMLInputElement;
  simplify: HTMLInputElement;
  simplifyLabel: HTMLElement;
  size: HTMLInputElement;
  sizeLabel: HTMLElement;
  replace: HTMLInputElement;
}

/**
 * Modal that wraps the traceImage pipeline. Takes a file, shows a live preview
 * of detected contours, lets the user tweak threshold/simplify/size, and
 * forwards the final scene-space paths back to the App.
 */
export class ImportDialog {
  private readonly dialog: HTMLDialogElement;
  private readonly dropZone: HTMLElement;
  private readonly fileInput: HTMLInputElement;
  private readonly previewImg: HTMLImageElement;
  private readonly previewPlaceholder: HTMLElement;
  private readonly previewTabsEl: HTMLElement;
  private readonly previewHintEl: HTMLElement;
  private readonly swatchesEl: HTMLElement;
  private readonly statsEl: HTMLElement;
  private readonly cancelBtn: HTMLButtonElement;
  private readonly importBtn: HTMLButtonElement;
  private readonly controls: Controls;

  private source: ImageBitmap | null = null;
  private currentResult: TraceResult | null = null;
  private color = "#ff2bd6";
  private thresholdAuto = true; // null threshold = Otsu
  private traceTimer = 0;
  private previewMode: PreviewMode = "trace";

  constructor(root: HTMLDialogElement, private readonly hooks: ImportDialogHooks) {
    this.dialog = root;
    this.render();

    this.dropZone = root.querySelector("#import-drop")!;
    this.fileInput = root.querySelector("#import-file") as HTMLInputElement;
    this.previewImg = root.querySelector("#import-preview-img") as HTMLImageElement;
    this.previewPlaceholder = root.querySelector("#import-preview-empty")!;
    this.previewTabsEl = root.querySelector("#import-preview-tabs")!;
    this.previewHintEl = root.querySelector("#import-preview-hint")!;
    this.swatchesEl = root.querySelector("#import-swatches")!;
    this.statsEl = root.querySelector("#import-stats")!;
    this.cancelBtn = root.querySelector("#import-cancel") as HTMLButtonElement;
    this.importBtn = root.querySelector("#import-apply") as HTMLButtonElement;

    this.controls = {
      threshold: root.querySelector("#import-threshold") as HTMLInputElement,
      thresholdLabel: root.querySelector("#import-threshold-label")!,
      autoThreshold: root.querySelector("#import-threshold-auto") as HTMLButtonElement,
      invert: root.querySelector("#import-invert") as HTMLInputElement,
      simplify: root.querySelector("#import-simplify") as HTMLInputElement,
      simplifyLabel: root.querySelector("#import-simplify-label")!,
      size: root.querySelector("#import-size") as HTMLInputElement,
      sizeLabel: root.querySelector("#import-size-label")!,
      replace: root.querySelector("#import-replace") as HTMLInputElement,
    };

    this.buildSwatches();
    this.buildPreviewTabs();
    this.wireEvents();
    this.updateButtons();
    this.refreshPreviewMode();
  }

  open(defaultColor: string): void {
    this.color = defaultColor;
    this.refreshSwatches();
    this.dialog.showModal();
  }

  close(): void {
    if (this.dialog.open) this.dialog.close();
  }

  // ── Rendering ─────────────────────────────────────────────────────

  private render(): void {
    this.dialog.innerHTML = `
      <form method="dialog" id="import-form">
        <header class="import-head">
          <h2>Import image</h2>
          <p class="import-sub">
            Drop in a high-contrast logo. The tracer binarizes the image and
            converts each outline into a neon tube.
          </p>
        </header>
        <div class="import-body">
          <div class="import-preview-col">
            <div id="import-preview-tabs" class="import-preview-tabs" role="tablist" hidden></div>
            <div class="import-preview">
              <div id="import-preview-empty" class="import-preview-empty">
                <div class="import-preview-icon">⇪</div>
                <div>Drag an image here or click to choose</div>
                <small>PNG, JPG, SVG, WebP</small>
              </div>
              <img id="import-preview-img" hidden alt="Trace preview" />
              <label id="import-drop" class="import-drop">
                <input id="import-file" type="file" accept="image/*" />
              </label>
            </div>
            <div id="import-preview-hint" class="import-preview-hint" hidden></div>
          </div>
          <div class="import-controls">
            <div class="import-row">
              <label>Threshold</label>
              <input id="import-threshold" type="range" min="0" max="255" step="1" value="128" />
              <span id="import-threshold-label" class="import-val">auto</span>
              <button id="import-threshold-auto" type="button" class="import-chip">Auto</button>
            </div>
            <div class="import-row">
              <label>Simplify</label>
              <input id="import-simplify" type="range" min="0.5" max="5" step="0.1" value="1.5" />
              <span id="import-simplify-label" class="import-val">1.5 px</span>
            </div>
            <div class="import-row">
              <label>Size</label>
              <input id="import-size" type="range" min="4" max="48" step="1" value="16" />
              <span id="import-size-label" class="import-val">16″</span>
            </div>
            <div class="import-row import-row-checks">
              <label class="import-check">
                <input id="import-invert" type="checkbox" />
                <span>Invert (light on dark)</span>
              </label>
              <label class="import-check">
                <input id="import-replace" type="checkbox" />
                <span>Replace current design</span>
              </label>
            </div>
            <div class="import-row import-row-color">
              <label>Color</label>
              <div id="import-swatches" class="import-swatches"></div>
            </div>
            <div id="import-stats" class="import-stats">No image loaded</div>
          </div>
        </div>
        <footer class="import-foot">
          <button id="import-cancel" type="button">Cancel</button>
          <button id="import-apply" type="button" class="import-primary" disabled>Import as neon</button>
        </footer>
      </form>
    `;
  }

  private buildSwatches(): void {
    this.swatchesEl.innerHTML = "";
    for (const entry of NEON_PALETTE) {
      const s = document.createElement("div");
      s.className = "swatch";
      s.style.background = entry.hex;
      s.style.color = entry.hex;
      s.dataset.color = entry.hex;
      s.title = entry.name;
      s.addEventListener("click", () => {
        this.color = entry.hex;
        this.refreshSwatches();
        // Neon preview is the only mode that reads the color — cheap to refresh
        // without a full retrace.
        if (this.previewMode === "neon") this.refreshPreviewImage();
      });
      this.swatchesEl.appendChild(s);
    }
  }

  private refreshSwatches(): void {
    for (const s of this.swatchesEl.querySelectorAll(".swatch")) {
      (s as HTMLElement).classList.toggle(
        "active",
        (s as HTMLElement).dataset.color?.toLowerCase() === this.color.toLowerCase(),
      );
    }
  }

  private buildPreviewTabs(): void {
    this.previewTabsEl.innerHTML = "";
    for (const mode of PREVIEW_MODES) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "import-tab";
      btn.dataset.mode = mode.id;
      btn.textContent = mode.label;
      btn.title = mode.hint;
      btn.setAttribute("role", "tab");
      btn.addEventListener("click", () => this.setPreviewMode(mode.id));
      this.previewTabsEl.appendChild(btn);
    }
  }

  private setPreviewMode(mode: PreviewMode): void {
    if (mode === this.previewMode) return;
    this.previewMode = mode;
    this.refreshPreviewMode();
  }

  /** Sync tab-active state + hint + image src for the current mode. */
  private refreshPreviewMode(): void {
    for (const btn of this.previewTabsEl.querySelectorAll<HTMLButtonElement>(".import-tab")) {
      btn.classList.toggle("active", btn.dataset.mode === this.previewMode);
      btn.setAttribute("aria-selected", String(btn.dataset.mode === this.previewMode));
    }
    const entry = PREVIEW_MODES.find((m) => m.id === this.previewMode);
    this.previewHintEl.textContent = entry?.hint ?? "";
    this.refreshPreviewImage();
  }

  /** Push the right image onto previewImg based on current mode + color. */
  private refreshPreviewImage(): void {
    const res = this.currentResult;
    if (!res) return;
    const src =
      this.previewMode === "neon"
        ? renderNeonPreview(res.render, this.color)
        : res.previews[this.previewMode];
    this.previewImg.src = src;
  }

  // ── Events ────────────────────────────────────────────────────────

  private wireEvents(): void {
    this.fileInput.addEventListener("change", () => {
      const file = this.fileInput.files?.[0];
      if (file) this.loadFile(file);
    });

    this.dropZone.addEventListener("dragover", (e) => {
      e.preventDefault();
      this.dropZone.classList.add("dragging");
    });
    this.dropZone.addEventListener("dragleave", () => {
      this.dropZone.classList.remove("dragging");
    });
    this.dropZone.addEventListener("drop", (e) => {
      e.preventDefault();
      this.dropZone.classList.remove("dragging");
      const file = e.dataTransfer?.files?.[0];
      if (file) this.loadFile(file);
    });

    this.controls.threshold.addEventListener("input", () => {
      this.thresholdAuto = false;
      this.controls.thresholdLabel.textContent = this.controls.threshold.value;
      this.scheduleRetrace();
    });
    this.controls.autoThreshold.addEventListener("click", () => {
      this.thresholdAuto = true;
      this.controls.thresholdLabel.textContent = "auto";
      this.scheduleRetrace();
    });
    this.controls.invert.addEventListener("change", () => this.scheduleRetrace());
    this.controls.simplify.addEventListener("input", () => {
      this.controls.simplifyLabel.textContent = `${this.controls.simplify.value} px`;
      this.scheduleRetrace();
    });
    this.controls.size.addEventListener("input", () => {
      this.controls.sizeLabel.textContent = `${this.controls.size.value}″`;
      this.scheduleRetrace();
    });

    this.cancelBtn.addEventListener("click", () => this.close());
    this.importBtn.addEventListener("click", () => this.apply());

    // Esc closes via native <dialog> behavior; intercept to also reset state.
    this.dialog.addEventListener("close", () => {
      this.reset();
      this.hooks.onClose?.();
    });
  }

  // ── Pipeline ──────────────────────────────────────────────────────

  private async loadFile(file: File): Promise<void> {
    try {
      this.source = await createImageBitmap(file);
    } catch (err) {
      this.statsEl.textContent = "Couldn't decode this image.";
      this.statsEl.classList.add("import-error");
      return;
    }
    this.statsEl.classList.remove("import-error");
    this.previewPlaceholder.hidden = true;
    this.previewImg.hidden = false;
    this.retrace();
  }

  private scheduleRetrace(): void {
    if (!this.source) return;
    window.clearTimeout(this.traceTimer);
    this.traceTimer = window.setTimeout(() => this.retrace(), 120);
  }

  private retrace(): void {
    if (!this.source) return;
    const opts = this.currentOptions();
    try {
      this.currentResult = traceImage(this.source, opts);
    } catch (err) {
      this.statsEl.textContent = "Trace failed.";
      this.statsEl.classList.add("import-error");
      return;
    }
    const res = this.currentResult;
    this.previewTabsEl.hidden = false;
    this.previewHintEl.hidden = false;
    this.refreshPreviewImage();
    if (this.thresholdAuto) {
      this.controls.threshold.value = String(res.usedThreshold);
      this.controls.thresholdLabel.textContent = `auto (${res.usedThreshold})`;
    }

    const sizeIn = Number(this.controls.size.value);
    const count = res.stats.contourCount;
    this.statsEl.textContent =
      count === 0
        ? "No contours detected — try inverting or adjusting the threshold."
        : `${count} tube${count === 1 ? "" : "s"} · ${res.stats.totalPoints} points · target width ≈ ${sizeIn}″`;
    this.updateButtons();
  }

  private currentOptions(): Partial<TraceOptions> {
    const sizeIn = Number(this.controls.size.value);
    const targetWidth = sizeIn * UNITS_PER_INCH;
    return {
      threshold: this.thresholdAuto ? null : Number(this.controls.threshold.value),
      invert: this.controls.invert.checked,
      simplifyEpsilon: Number(this.controls.simplify.value),
      targetWidth,
      blurRadius: DEFAULT_TRACE_OPTIONS.blurRadius,
      minContourLength: DEFAULT_TRACE_OPTIONS.minContourLength,
      maxDimension: DEFAULT_TRACE_OPTIONS.maxDimension,
    };
  }

  private updateButtons(): void {
    this.importBtn.disabled = !this.currentResult || this.currentResult.contours.length === 0;
  }

  private apply(): void {
    if (!this.currentResult || this.currentResult.contours.length === 0) return;
    this.hooks.onImport({
      contours: this.currentResult.contours,
      color: this.color,
      replace: this.controls.replace.checked,
    });
    this.close();
  }

  private reset(): void {
    this.source = null;
    this.currentResult = null;
    this.fileInput.value = "";
    this.previewImg.hidden = true;
    this.previewImg.removeAttribute("src");
    this.previewPlaceholder.hidden = false;
    this.previewTabsEl.hidden = true;
    this.previewHintEl.hidden = true;
    this.previewMode = "trace";
    this.refreshPreviewMode();
    this.controls.replace.checked = false;
    this.thresholdAuto = true;
    this.controls.thresholdLabel.textContent = "auto";
    this.controls.threshold.value = "128";
    this.controls.simplify.value = "1.5";
    this.controls.simplifyLabel.textContent = "1.5 px";
    this.controls.size.value = "16";
    this.controls.sizeLabel.textContent = "16″";
    this.controls.invert.checked = false;
    this.statsEl.textContent = "No image loaded";
    this.statsEl.classList.remove("import-error");
    this.updateButtons();
  }
}
